import React, { useEffect, useState } from 'react';
import { Close, Download, OpenInNew } from '@mui/icons-material';
import { urlDeArchivo } from '../../config';
import { tipoDeVista, motivoSinVista, formatFileSize } from '../../services/spaceMediaService';
import './visorDocumento.scss';

/**
 * Visor de documentos a pantalla completa.
 *
 * ---------------------------------------------------------------------------
 * QUÉ RESUELVE
 * ---------------------------------------------------------------------------
 * Hasta ahora, para leer cualquier documento de un espacio había que
 * descargarlo: la barra lateral solo tenía botón de descarga, y el visor de
 * la pantalla de administración de espacios estaba roto (llamaba a una
 * función `canPreviewFile` que nunca se llegó a escribir, así que la pestaña
 * de documentos se caía apenas había un archivo que mostrar).
 *
 * Acá se muestra dentro de la plataforma todo lo que el navegador sabe
 * mostrar por su cuenta:
 *
 *   - PDF        -> visor de PDF del propio navegador, en un <iframe>
 *   - Imágenes   -> la imagen, ajustada a la pantalla
 *   - TXT / CSV  -> el texto del archivo
 *
 * Word, Excel y PowerPoint no se pueden abrir en un navegador sin mandarlos
 * a un servicio externo (Office Online, Google Docs), y estos documentos son
 * internos: no se sacan de la red de la empresa. Para esos se dice con
 * claridad que hay que descargarlos, en vez de dejar un visor en blanco.
 *
 * Nota para quien venga después: para que el <iframe> funcione hizo falta
 * además cambiar una cabecera del backend. Ver backend/app.js, el bloque de
 * "Cabeceras de seguridad básicas".
 */
const VisorDocumento = ({ documento, onCerrar, onDescargar }) => {
  const [textoPlano, setTextoPlano] = useState(null);
  const [errorTexto, setErrorTexto] = useState(null);

  const url = urlDeArchivo(documento?.fileUrl);
  const vista = tipoDeVista(documento?.fileType, documento?.fileName);
  const programa = motivoSinVista(documento?.fileType, documento?.fileName);

  // Cerrar con Escape: en un visor a pantalla completa es lo que todo el
  // mundo intenta primero.
  useEffect(() => {
    const alPulsar = (e) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [onCerrar]);

  // El texto plano no se puede meter en un <iframe> y dejarlo ahí: hay que
  // traerlo para poder mostrarlo con el tipo de letra y el ancho de la
  // plataforma. Se limita el tamaño porque un .csv de exportación puede
  // pesar varios megas y colgaría la pestaña.
  useEffect(() => {
    if (vista !== 'texto' || !url) return;
    let cancelado = false;
    setTextoPlano(null);
    setErrorTexto(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`El servidor respondió ${res.status}`);
        return res.text();
      })
      .then((texto) => {
        if (cancelado) return;
        const LIMITE = 200000; // ~200 KB de texto
        setTextoPlano(
          texto.length > LIMITE
            ? texto.slice(0, LIMITE) + '\n\n[…] Archivo demasiado largo para mostrarlo completo. Descárgalo para verlo entero.'
            : texto
        );
      })
      .catch((err) => {
        if (!cancelado) setErrorTexto(err.message);
      });

    return () => { cancelado = true; };
  }, [url, vista]);

  if (!documento) return null;

  const cuerpo = () => {
    if (vista === 'pdf') {
      return (
        <iframe
          src={`${url}#view=FitH`}
          title={documento.fileName}
          className="visor-marco"
        />
      );
    }

    if (vista === 'imagen') {
      return (
        <div className="visor-imagen">
          <img src={url} alt={documento.fileName} />
        </div>
      );
    }

    if (vista === 'texto') {
      if (errorTexto) {
        return (
          <div className="visor-aviso">
            <p>No se pudo leer el archivo.</p>
            <p className="visor-detalle">{errorTexto}</p>
          </div>
        );
      }
      if (textoPlano === null) {
        return <div className="visor-aviso"><p>Cargando documento…</p></div>;
      }
      return <pre className="visor-texto">{textoPlano}</pre>;
    }

    return (
      <div className="visor-aviso">
        <p className="visor-aviso-titulo">
          {programa
            ? `Los documentos de ${programa} no se pueden ver dentro del navegador.`
            : 'Este tipo de documento no se puede ver dentro del navegador.'}
        </p>
        <p>Descárgalo para abrirlo con el programa correspondiente.</p>
      </div>
    );
  };

  return (
    <div className="visor-documento" onClick={onCerrar}>
      <div className="visor-caja" onClick={(e) => e.stopPropagation()}>
        <div className="visor-encabezado">
          <div className="visor-titulo">
            <h3 title={documento.fileName}>{documento.fileName}</h3>
            {documento.fileSize ? <small>{formatFileSize(documento.fileSize)}</small> : null}
          </div>

          <div className="visor-acciones">
            {/* Abrir aparte sirve para imprimir el PDF o verlo más grande. */}
            <a
              className="visor-btn"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir en una pestaña nueva"
            >
              <OpenInNew fontSize="small" />
            </a>
            <button
              className="visor-btn"
              onClick={() => onDescargar(documento)}
              title="Descargar"
            >
              <Download fontSize="small" />
            </button>
            <button className="visor-btn visor-cerrar" onClick={onCerrar} title="Cerrar">
              <Close fontSize="small" />
            </button>
          </div>
        </div>

        <div className="visor-cuerpo">{cuerpo()}</div>
      </div>
    </div>
  );
};

export default VisorDocumento;
